import {
  AllocationValueTypeEnum,
  AllocationsSelector,
  AssetTransactionTypeEnum,
  Box,
  ButtonVariants,
  DEFAULT_SUBACCOUNT_ALLOCATION,
  DateTimePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  FormControlSizes,
  FormGroup,
  HStack,
  Input,
  NOW,
  NotificationVariants,
  NumberInput,
  ProductTypeEnum,
  SearchSelect,
  Stack,
  Text,
  TextDividerSimple,
  ToggleGroup,
  frontendAllocationsToBackendAllocations,
  getErrorMessage,
  isAssetSpotSecurity,
  productTypeLabel,
  useAssetsContext,
  useCurrenciesContext,
  useGlobalToasts,
  useMarketAccountsContext,
  useSecuritiesContext,
  type Allocation,
  type AllocationChange,
  type AssetTransactionTypeEnum as AssetTransactionTypeEnumType,
  type ErrorResponse,
  type ToggleGroupOption,
  type useDisclosureWithContext,
} from '@talos/kyoko';
import { identity, isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useSymbolSelectorWidth } from '../../../components/SymbolSelector/useSymbolSelectorWidth';
import { useSubAccounts } from '../../../providers';
import type { UpdatePositionPrimer } from '../PositionsV3/UpdatePositionsDialog';
import type { CreateAssetTransactionItem } from './assetTransactionsApi';
import { useCreateAssetTransactionMutation } from './assetTransactionsApi';
import {
  EXCLUDED_ADJUST_POSITION_TYPES,
  isAllowedAssetTransactionProductType,
  prettifyAssetTransactionTypeEnum,
  requiresPositionAsset,
} from './assetTransactionTypeOptions';
import { useAdjustMarketAccountPositionsValidation } from './useAdjustMarketAccountPositionsValidation';
import { useUpdateBalancesAccounts } from './useUpdateBalancesAccounts';

export type AllocationMode = 'infer' | 'manual' | 'doNotAllocate';

export interface AdjustMarketAccountPositionsForm {
  type?: AssetTransactionTypeEnumType;
  marketAccount?: string;
  asset?: string;
  positionAsset?: string;
  amount?: string;
  timestamp?: Date;
  allocationMode: AllocationMode;
  subAccountAllocations: Allocation[];
  allocationValueType: AllocationValueTypeEnum;
  avgCostCurrency?: string;
  avgCost?: string;
  comment?: string;
}

export type AdjustMarketAccountPositionsDisclosure = ReturnType<typeof useDisclosureWithContext<UpdatePositionPrimer>>;

interface AdjustMarketAccountPositionsDialogProps {
  disclosure: AdjustMarketAccountPositionsDisclosure;
  customMarketAccountOptions?: string[];
}

const ALLOCATION_MODE_OPTIONS: ToggleGroupOption<AllocationMode>[] = [
  { value: 'infer', displayName: 'Infer using Allocation Controls' },
  { value: 'manual', displayName: 'Allocate Manually' },
  { value: 'doNotAllocate', displayName: 'Do Not Allocate' },
];

const EXCLUDED_ADJUST_POSITION_TYPES_SET = new Set<AssetTransactionTypeEnumType>(EXCLUDED_ADJUST_POSITION_TYPES);

const TYPE_OPTIONS = (Object.values(AssetTransactionTypeEnum) as AssetTransactionTypeEnumType[]).filter(
  t => !EXCLUDED_ADJUST_POSITION_TYPES_SET.has(t)
);

export const AdjustMarketAccountPositionsDialog = ({
  disclosure,
  customMarketAccountOptions,
}: AdjustMarketAccountPositionsDialogProps) => (
  <Dialog
    {...disclosure}
    title={disclosure.context?.Asset ? 'Adjust Market Account Position' : 'Adjust Market Account Positions'}
    width={420}
    showConfirm={false}
    showCancel={false}
    controlledContent
    showClose
    suppressAutoEnterForm
  >
    {disclosure.context && (
      <AdjustMarketAccountPositionsDialogContent
        disclosure={disclosure}
        context={disclosure.context}
        customMarketAccountOptions={customMarketAccountOptions}
      />
    )}
  </Dialog>
);

const getInitialForm = (
  primer: UpdatePositionPrimer,
  resolveSecurityOrCurrency: ReturnType<typeof useAssetsContext>['resolveSecurityOrCurrency']
): AdjustMarketAccountPositionsForm => {
  // Context-menu prefill can set a spot pair; drop it (and its cost basis) so it can't bypass the Asset selector filter.
  const asset =
    primer.Asset && !isAssetSpotSecurity(resolveSecurityOrCurrency(primer.Asset)) ? primer.Asset : undefined;

  return {
    type: undefined,
    marketAccount: primer.MarketAccount,
    asset,
    positionAsset: undefined,
    amount: undefined,
    timestamp: undefined,
    allocationMode: 'infer',
    subAccountAllocations: [DEFAULT_SUBACCOUNT_ALLOCATION],
    allocationValueType: AllocationValueTypeEnum.Percentage,
    // Cost basis belongs to the primed asset — only keep it when that asset is accepted.
    avgCostCurrency: asset ? primer.AvgCostCurrency : undefined,
    avgCost: asset ? primer.AvgCost : undefined,
    comment: undefined,
  };
};

interface AdjustMarketAccountPositionsDialogContentProps {
  disclosure: AdjustMarketAccountPositionsDisclosure;
  context: UpdatePositionPrimer;
  customMarketAccountOptions?: string[];
}

const AdjustMarketAccountPositionsDialogContent = ({
  disclosure,
  context,
  customMarketAccountOptions,
}: AdjustMarketAccountPositionsDialogContentProps) => {
  const { add: addToast } = useGlobalToasts();
  const [createAssetTransaction, { isLoading }] = useCreateAssetTransactionMutation();

  const { assetsList, getAssetDisplaySymbol, resolveSecurityOrCurrency } = useAssetsContext();
  const { currenciesList } = useCurrenciesContext();
  const { securitiesList } = useSecuritiesContext();
  const symbolSelectorDropdownWidth = useSymbolSelectorWidth();

  const getAssetGroup = useCallback(
    (asset: string) => {
      const { currency, security } = resolveSecurityOrCurrency(asset);
      if (currency) {
        return productTypeLabel(ProductTypeEnum.Spot);
      }
      if (security) {
        return productTypeLabel(security.ProductType);
      }
      return 'Unknown';
    },
    [resolveSecurityOrCurrency]
  );

  const getAssetDescription = useCallback(
    (asset: string) => {
      const { currency, security } = resolveSecurityOrCurrency(asset);
      return (currency ?? security)?.Description ?? '';
    },
    [resolveSecurityOrCurrency]
  );
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  const { accounts: internalMarketAccountNames } = useUpdateBalancesAccounts();
  const { allSubAccountBooks, subAccountsByName } = useSubAccounts();

  const [form, setForm] = useState<AdjustMarketAccountPositionsForm>(() =>
    getInitialForm(context, resolveSecurityOrCurrency)
  );

  // Reset form on each open (openId is a new UUID every time open() is called)
  useEffect(() => {
    setForm(getInitialForm(context, resolveSecurityOrCurrency));
  }, [disclosure.openId, context, resolveSecurityOrCurrency]);

  const { errors, touched, setTouched, setAllTouched } = useAdjustMarketAccountPositionsValidation(form);

  const updateForm = useCallback((update: Partial<AdjustMarketAccountPositionsForm>) => {
    setForm(prev => ({ ...prev, ...update }));
  }, []);

  const showPositionAsset = requiresPositionAsset(form.type);

  const marketAccountOptions = useMemo(() => {
    if (customMarketAccountOptions) {
      return customMarketAccountOptions;
    }
    if (internalMarketAccountNames?.length) {
      return internalMarketAccountNames;
    }
    return marketAccountsByMarket.get('internal')?.map(ma => ma.Name) ?? [];
  }, [customMarketAccountOptions, internalMarketAccountNames, marketAccountsByMarket]);

  const assetOptions = useMemo(
    () => assetsList.filter(asset => !isAssetSpotSecurity(resolveSecurityOrCurrency(asset.Symbol))).map(a => a.Symbol),
    [assetsList, resolveSecurityOrCurrency]
  );
  const currencyOptions = useMemo(() => currenciesList.map(c => c.Symbol), [currenciesList]);

  const positionAssetOptions = useMemo(() => {
    if (!showPositionAsset) {
      return [];
    }
    if (!form.marketAccount) {
      return [];
    }
    const marketAccount = marketAccountsByName.get(form.marketAccount);
    if (!marketAccount) {
      return [];
    }
    return securitiesList
      .filter(
        security =>
          security.Markets?.includes(marketAccount.Market) && isAllowedAssetTransactionProductType(security.ProductType)
      )
      .map(security => security.Symbol);
  }, [showPositionAsset, form.marketAccount, marketAccountsByName, securitiesList]);

  const subAccountOptions = useMemo(() => allSubAccountBooks?.map(s => s.Name) ?? [], [allSubAccountBooks]);

  const getMarketAccountLabel = useCallback(
    (name: string | undefined) => (name ? (marketAccountsByName.get(name)?.DisplayName ?? name) : (name ?? '')),
    [marketAccountsByName]
  );

  const handleConfirm = useCallback(async () => {
    const trimmedAvgCost = form.avgCost?.trim();
    if (
      !isEmpty(errors) ||
      !form.marketAccount ||
      !form.asset ||
      !form.amount ||
      !form.timestamp ||
      !form.type ||
      (requiresPositionAsset(form.type) && !form.positionAsset) ||
      (Boolean(trimmedAvgCost) && !form.avgCostCurrency)
    ) {
      return;
    }

    let subAccount: string | undefined;
    let allocation = undefined;

    if (form.allocationMode === 'manual') {
      const { subAccountAllocations, allocationValueType, amount } = form;
      if (subAccountAllocations.length > 1) {
        allocation = frontendAllocationsToBackendAllocations({
          subAccountAllocations,
          subAccounts: allSubAccountBooks,
          allocationValueType,
          quantity: amount,
        });
      } else {
        subAccount = subAccountAllocations[0]?.subAccount || undefined;
      }
    }

    const payload: CreateAssetTransactionItem = {
      TransactionID: uuid(),
      TransactTime: form.timestamp.toISOString(),
      Type: form.type,
      MarketAccount: form.marketAccount,
      Asset: form.asset,
      Amount: form.amount,
      AllocationStatus: form.allocationMode === 'infer' ? 'Unallocated' : 'Manual',
    };
    if (requiresPositionAsset(form.type) && form.positionAsset != null) {
      payload.PositionAsset = form.positionAsset;
    }
    if (subAccount != null) {
      payload.SubAccount = subAccount;
    }
    if (allocation != null) {
      payload.Allocation = allocation;
    }
    const trimmedComment = form.comment?.trim();
    if (trimmedComment) {
      payload.Comment = trimmedComment;
    }
    if (trimmedAvgCost) {
      payload.Price = trimmedAvgCost;
      payload.PriceCurrency = form.avgCostCurrency;
    }

    try {
      const created = await createAssetTransaction([payload]).unwrap();

      addToast({
        text:
          created.length === 1
            ? 'Successfully applied 1 transaction.'
            : `Successfully applied ${created.length} transactions.`,
        variant: NotificationVariants.Positive,
      });
      disclosure.close();
    } catch (e: unknown) {
      const detail = getErrorMessage(e as ErrorResponse) ?? (e instanceof Error ? e.message : undefined) ?? String(e);
      addToast({
        text: `Failed to post balance adjustment: ${detail}`,
        variant: NotificationVariants.Negative,
      });
    }
  }, [errors, form, allSubAccountBooks, createAssetTransaction, addToast, disclosure]);

  const handleAllocationsChange = useCallback(
    (newAllocations: Allocation[], _change: AllocationChange) => {
      updateForm({ subAccountAllocations: newAllocations });
      setTouched(prev => ({ ...prev, subAccountAllocations: true }));
    },
    [updateForm, setTouched]
  );

  const handleAllocationsValueTypeChange = useCallback(
    (newType: AllocationValueTypeEnum) => updateForm({ allocationValueType: newType }),
    [updateForm]
  );

  return (
    <>
      <DialogContent overflow="hidden" style={{ order: 2, height: '100%', overflow: 'auto' }}>
        <Stack gap="spacing0" w="100%">
          <Text textAlign="left" size="fontSizeSm" mb="spacing16">
            Create a new Asset Transaction for a Market Account.
          </Text>

          <Box my="spacing4">
            <TextDividerSimple text="Details" />
          </Box>

          <FormGroup label="Type" required error={touched.type && errors.type ? errors.type : undefined}>
            <SearchSelect
              selection={form.type}
              options={TYPE_OPTIONS}
              onChange={value => {
                updateForm({
                  type: value,
                  ...(!requiresPositionAsset(value) ? { positionAsset: undefined } : {}),
                });
                setTouched(prev => ({ ...prev, type: true }));
              }}
              getLabel={prettifyAssetTransactionTypeEnum}
              data-testid="adjust-market-account-positions-type"
            />
          </FormGroup>

          <FormGroup
            label="Market Account"
            required
            error={touched.marketAccount && errors.marketAccount ? errors.marketAccount : undefined}
          >
            <SearchSelect
              selection={form.marketAccount}
              options={marketAccountOptions}
              onChange={value => {
                updateForm({ marketAccount: value, positionAsset: undefined });
                setTouched(prev => ({ ...prev, marketAccount: true }));
              }}
              getLabel={getMarketAccountLabel}
              data-testid="adjust-market-account-positions-market-account"
            />
          </FormGroup>

          <HStack gap="spacing16" alignItems="flex-start">
            <FormGroup
              label="Asset"
              required
              flexBasis="50%"
              error={touched.asset && errors.asset ? errors.asset : undefined}
            >
              <SearchSelect
                selection={form.asset}
                options={assetOptions}
                onChange={value => {
                  updateForm({ asset: value });
                  setTouched(prev => ({ ...prev, asset: true }));
                }}
                getGroup={getAssetGroup}
                showGroupTabs
                getLabel={getAssetDisplaySymbol}
                getDescription={getAssetDescription}
                data-testid="adjust-market-account-positions-asset"
                dropdownWidth={symbolSelectorDropdownWidth}
                maxHeight={400}
              />
            </FormGroup>

            <FormGroup
              label="Amount"
              required
              flexBasis="50%"
              error={touched.amount && errors.amount ? errors.amount : undefined}
              tooltip="Use negative values for withdrawals or reductions."
            >
              <NumberInput
                value={form.amount ?? ''}
                onChange={value => {
                  updateForm({ amount: value });
                  setTouched(prev => ({ ...prev, amount: true }));
                }}
                data-testid="adjust-market-account-positions-amount"
              />
            </FormGroup>
          </HStack>

          <HStack gap="spacing16" alignItems="flex-start">
            <FormGroup
              label="Avg Cost Currency"
              required={Boolean(form.avgCost?.trim())}
              flexBasis="50%"
              error={touched.avgCostCurrency && errors.avgCostCurrency ? errors.avgCostCurrency : undefined}
            >
              <SearchSelect
                selection={form.avgCostCurrency}
                options={currencyOptions}
                onChange={value => {
                  updateForm({ avgCostCurrency: value });
                  setTouched(prev => ({ ...prev, avgCostCurrency: true }));
                }}
                getLabel={identity}
                showClear
                data-testid="adjust-market-account-positions-avg-cost-currency"
              />
            </FormGroup>

            <FormGroup
              label="Avg Cost"
              flexBasis="50%"
              error={touched.avgCost && errors.avgCost ? errors.avgCost : undefined}
            >
              <NumberInput
                value={form.avgCost ?? ''}
                onChange={value => {
                  updateForm({ avgCost: value });
                  setTouched(prev => ({ ...prev, avgCost: true }));
                }}
                data-testid="adjust-market-account-positions-avg-cost"
              />
            </FormGroup>
          </HStack>

          {showPositionAsset && (
            <FormGroup
              label="Position Asset"
              required
              error={touched.positionAsset && errors.positionAsset ? errors.positionAsset : undefined}
            >
              <SearchSelect
                selection={form.positionAsset}
                options={positionAssetOptions}
                onChange={value => {
                  updateForm({ positionAsset: value });
                  setTouched(prev => ({ ...prev, positionAsset: true }));
                }}
                getGroup={getAssetGroup}
                showGroupTabs
                getLabel={getAssetDisplaySymbol}
                getDescription={getAssetDescription}
                data-testid="adjust-market-account-positions-position-asset"
                dropdownWidth={symbolSelectorDropdownWidth}
                maxHeight={400}
              />
            </FormGroup>
          )}

          <FormGroup
            label="Update Timestamp"
            required
            error={touched.timestamp && errors.timestamp ? errors.timestamp : undefined}
          >
            <DateTimePicker
              value={form.timestamp ?? null}
              onChange={value => {
                updateForm({ timestamp: value ?? undefined });
                setTouched(prev => ({ ...prev, timestamp: true }));
              }}
              portalize={false}
              showMilliseconds
              shortcuts={{ [NOW]: 'Now' }}
              data-testid="adjust-market-account-positions-timestamp"
            />
          </FormGroup>

          <FormGroup label="Comment (optional)" error={touched.comment && errors.comment ? errors.comment : undefined}>
            <Input
              value={form.comment ?? ''}
              autoComplete="off"
              onChange={e => {
                updateForm({ comment: e.target.value });
                setTouched(prev => ({ ...prev, comment: true }));
              }}
              data-testid="adjust-market-account-positions-comment"
            />
          </FormGroup>

          <Box my="spacing4">
            <TextDividerSimple text="Allocation" />
          </Box>
          <FormGroup label="Allocation Mode" required mb="spacing8">
            <ToggleGroup
              options={ALLOCATION_MODE_OPTIONS}
              value={form.allocationMode}
              onChange={mode => updateForm({ allocationMode: mode })}
              size={FormControlSizes.Small}
              data-testid="adjust-market-account-positions-allocation-mode"
              defaultSelectedVariant={ButtonVariants.Priority}
            />
          </FormGroup>

          {form.allocationMode === 'manual' && (
            <AllocationsSelector
              subAccountAllocations={form.subAccountAllocations}
              allocationValueType={form.allocationValueType}
              subAccountOptions={subAccountOptions}
              subAccountsByName={subAccountsByName}
              onAllocationsChange={handleAllocationsChange}
              onAllocationsValueTypeChange={handleAllocationsValueTypeChange}
              useAllocations
              showAddAllocationsButton
              required
              touched={{ subAccountAllocations: touched.subAccountAllocations }}
              errors={{ subAccountAllocations: errors.subAccountAllocations }}
              quantityCurrency={form.asset}
            />
          )}
        </Stack>
      </DialogContent>
      <Box style={{ order: 3 }}>
        <DialogFooter
          showCancel
          showConfirm
          confirmLabel="Post"
          confirmDisabled={!isEmpty(errors) || isLoading}
          confirmLoading={isLoading}
          handleConfirm={() => void handleConfirm()}
          handleCancel={disclosure.close}
          onConfirmMouseOver={setAllTouched}
        />
      </Box>
    </>
  );
};
